import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Timer,
  Zap,
  Database,
  Bot,
  Users,
  Wrench,
  FileText,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Activity,
  StopCircle,
  Clock,
  BarChart3,
  Copy,
  ExternalLink,
  Maximize,
  Minimize,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { useExecutionRecordsApi, ExecutionRecord } from '../apis/execution-records-apis';
import { useUsersApi } from '../apis/users-apis';
import { UserData } from '../types';

const ExecutionDetailsPage: React.FC = () => {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  
  // State
  const [execution, setExecution] = useState<ExecutionRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userMap, setUserMap] = useState<Map<string, string>>(new Map());
  const [activeTab, setActiveTab] = useState<'overview' | 'aiLog' | 'executionLog'>('overview');
  const [aiLogContent, setAiLogContent] = useState<string | null>(null);
  const [executionLogContent, setExecutionLogContent] = useState<string | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  
  const { getExecutionRecords } = useExecutionRecordsApi();
  const { getUsers } = useUsersApi();

  // Fetch execution details
  useEffect(() => {
    const fetchExecutionDetails = async () => {
      if (!executionId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Get all executions and find the specific one
        const response = await getExecutionRecords('', 1000, 1);
        const foundExecution = response.items.find(item => item.executionId === executionId);
        
        if (foundExecution) {
          setExecution(foundExecution);
        } else {
          setError('Execution not found');
        }
      } catch (err) {
        console.error('Error fetching execution details:', err);
        setError('Failed to load execution details');
      } finally {
        setLoading(false);
      }
    };

    fetchExecutionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await getUsers();
        const userMap = new Map();
        usersData.forEach((user: UserData) => {
          userMap.set(user.userId, user.email);
        });
        setUserMap(userMap);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch log content
  const fetchLogContent = async (logUrl: string, type: 'ai' | 'execution') => {
    setLogsLoading(true);
    setLogError(null);
    
    try {
      const response = await fetch(logUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch log: ${response.statusText}`);
      }
      
      const content = await response.text();
      
      if (type === 'ai') {
        setAiLogContent(content);
      } else {
        setExecutionLogContent(content);
      }
    } catch (err) {
      console.error(`Error fetching ${type} log:`, err);
      setLogError(`Failed to load ${type} log`);
    } finally {
      setLogsLoading(false);
    }
  };

  // Load logs when tab changes
  useEffect(() => {
    if (!execution) return;
    
    if (activeTab === 'aiLog' && execution.aiLog && !aiLogContent) {
      fetchLogContent(execution.aiLog, 'ai');
    } else if (activeTab === 'executionLog' && execution.executionLog && !executionLogContent) {
      fetchLogContent(execution.executionLog, 'execution');
    }
  }, [activeTab, execution, aiLogContent, executionLogContent]);

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'in_progress':
        return <Activity className="h-5 w-5 text-blue-500 animate-pulse" />;
      case 'kill_switch':
        return <StopCircle className="h-5 w-5 text-orange-500" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return <Badge className="bg-green-50 text-green-700 border-green-200">Success</Badge>;
      case 'error':
        return <Badge className="bg-red-50 text-red-700 border-red-200">Error</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-50 text-blue-700 border-blue-200">In Progress</Badge>;
      case 'kill_switch':
        return <Badge className="bg-orange-50 text-orange-700 border-orange-200">Terminated</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
    }
  };

  const formatDuration = (seconds: number | string) => {
    const numSeconds = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
    if (!numSeconds || isNaN(numSeconds) || numSeconds <= 0) return '0s';
    
    // Handle unreasonably large durations (more than 24 hours = likely data error)
    if (numSeconds > 86400) {
      console.warn(`Unusually large duration detected: ${numSeconds} seconds`);
      return `${Math.round(numSeconds / 86400)}d`;
    }
    
    if (numSeconds < 60) return `${Math.round(numSeconds)}s`;
    if (numSeconds < 3600) return `${Math.round(numSeconds / 60)}m`;
    return `${Math.round(numSeconds / 3600)}h`;
  };

  const formatCost = (cost: number | string) => {
    const numCost = typeof cost === 'string' ? parseFloat(cost) : cost;
    if (!numCost || isNaN(numCost)) return '$0.00';
    return `$${numCost.toFixed(4)}`;
  };

  const formatTokens = (tokens: number | string) => {
    const numTokens = typeof tokens === 'string' ? parseFloat(tokens) : tokens;
    if (!numTokens || isNaN(numTokens)) return '0';
    if (numTokens < 1000) return Math.round(numTokens).toString();
    if (numTokens < 1000000) return `${(numTokens / 1000).toFixed(1)}K`;
    return `${(numTokens / 1000000).toFixed(1)}M`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatCSVContent = (content: string) => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return <div className="text-gray-500">No content available</div>;

    const headers = lines[0].split('*').map(header => header.trim());
    const rows = lines.slice(1).map(line => {
      const values = line.split('*').map(value => value.trim());
      return values;
    });

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner />
          <p className="mt-4 text-gray-600">Loading execution details...</p>
        </div>
      </div>
    );
  }

  if (error || !execution) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/studio/executions')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Executions
            </Button>
          </div>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-8 text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-red-900 mb-2">Execution Not Found</h3>
              <p className="text-red-700">{error || 'The requested execution could not be found.'}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${fullscreen ? 'fixed inset-0 z-50' : 'p-3 md:p-6'}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/studio/executions')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Execution Details</h1>
                <p className="text-gray-600 text-sm font-mono">{execution.executionId}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => copyToClipboard(execution.executionId)}
                className="flex items-center gap-2"
                size="sm"
              >
                <Copy className="h-4 w-4" />
                Copy ID
              </Button>
              {(activeTab === 'aiLog' || activeTab === 'executionLog') && (
                <Button
                  variant="outline"
                  onClick={() => setFullscreen(!fullscreen)}
                  className="flex items-center gap-2"
                  size="sm"
                >
                  {fullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                  {fullscreen ? 'Exit' : 'Fullscreen'}
                </Button>
              )}
            </div>
          </div>

          {/* Status and Basic Info */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  {getStatusIcon(execution.status || '')}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{execution.playbookName || 'Unknown Playbook'}</h2>
                    <p className="text-gray-600">
                      Executed on {new Date(execution.executionDate).toLocaleString()}
                    </p>
                  </div>
                </div>
                {getStatusBadge(execution.status || '')}
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Timer className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">Duration</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{formatDuration(execution.duration || 0)}</p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">Tokens</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">{formatTokens(execution.totalTokens || 0)}</p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-600">Cost</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-900">{formatCost(execution.estimatedCost || 0)}</p>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-600">Conversations</span>
                  </div>
                  <p className="text-2xl font-bold text-orange-900">{execution.conversationCount || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              {execution.aiLog && (
                <button
                  onClick={() => setActiveTab('aiLog')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'aiLog'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  AI Log
                </button>
              )}
              {execution.executionLog && (
                <button
                  onClick={() => setActiveTab('executionLog')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'executionLog'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Execution Log
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Execution Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Execution Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Owner</label>
                  <p className="text-gray-900">{userMap.get(execution.ownerId || '') || execution.ownerId || 'Unknown'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Playbook Description</label>
                  <p className="text-gray-900">{execution.playbookDescription || 'No description available'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">AI Model</label>
                  <p className="text-gray-900">{execution.aiModel || 'Unknown'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Max Turns</label>
                  <p className="text-gray-900">{execution.maxTurns || 'Not specified'}</p>
                </div>
                
                {execution.inputDataSummary && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Input Summary</label>
                    <p className="text-gray-900 text-sm">{execution.inputDataSummary}</p>
                  </div>
                )}
                
                {execution.error && (
                  <div>
                    <label className="text-sm font-medium text-red-500">Error</label>
                    <p className="text-red-700 text-sm bg-red-50 p-3 rounded-md">{execution.error}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Team & Resources */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team & Resources
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Agents Involved</label>
                  <p className="text-gray-900">{execution.agentsCount || 0} agents</p>
                  {execution.agentsInvolved && execution.agentsInvolved.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {execution.agentsInvolved.map((agent, index) => (
                        <Badge key={index} variant="outline">{agent}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Tools Used</label>
                  <p className="text-gray-900">{execution.toolsCount || 0} tools</p>
                  {execution.toolsUsed && execution.toolsUsed.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {execution.toolsUsed.map((tool, index) => (
                        <Badge key={index} variant="outline">{tool}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Models Used</label>
                  {execution.modelsUsed && execution.modelsUsed.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {execution.modelsUsed.map((model, index) => (
                        <Badge key={index} variant="outline">{model}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-900">{execution.aiModel || 'Unknown'}</p>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Tool Invocations</label>
                  <p className="text-gray-900">{execution.toolInvocationCount || 0}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Error Count</label>
                  <p className="text-gray-900">{execution.errorCount || 0}</p>
                </div>
              </CardContent>
            </Card>

            {/* Token Usage Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Token Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Input Tokens</p>
                    <p className="text-xl font-semibold text-blue-600">{formatTokens(execution.inputTokens || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Output Tokens</p>
                    <p className="text-xl font-semibold text-green-600">{formatTokens(execution.outputTokens || 0)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500">Total Tokens</p>
                    <p className="text-xl font-semibold text-purple-600">{formatTokens(execution.totalTokens || 0)}</p>
                  </div>
                </div>
                
                <div className="pt-4">
                  <label className="text-sm font-medium text-gray-500">Average Tokens per Conversation</label>
                  <p className="text-gray-900">{formatTokens(execution.avgTokensPerConversation || 0)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Additional Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Additional Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Input Type</label>
                  <p className="text-gray-900">{execution.inputType || 'Unknown'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Input Complexity</label>
                  {execution.inputComplexity && (
                    <Badge 
                      className={
                        execution.inputComplexity === 'High' ? 'bg-red-50 text-red-700 border-red-200' :
                        execution.inputComplexity === 'Medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                        'bg-green-50 text-green-700 border-green-200'
                      }
                    >
                      {execution.inputComplexity}
                    </Badge>
                  )}
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Environment</label>
                  <p className="text-gray-900">{execution.environment || 'Unknown'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Reasoning Steps</label>
                  <p className="text-gray-900">{execution.reasoningStepsCount || 0}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* AI Log Content */}
        {activeTab === 'aiLog' && (
          <Card className={fullscreen ? 'h-screen' : ''}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Conversation Log
              </CardTitle>
              <div className="flex items-center gap-2">
                {execution.aiLog && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(execution.aiLog, '_blank')}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Original
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogContent(execution.aiLog!, 'ai')}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className={`${fullscreen ? 'h-full overflow-auto' : ''}`}>
              {logsLoading ? (
                <div className="flex justify-center items-center p-8">
                  <Spinner />
                </div>
              ) : logError ? (
                <div className="p-4 bg-red-50 text-red-700 rounded-md">
                  <p>{logError}</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 overflow-auto">
                  <pre className="font-mono text-sm whitespace-pre-wrap">
                    {aiLogContent || 'No AI log content available'}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Execution Log Content */}
        {activeTab === 'executionLog' && (
          <Card className={fullscreen ? 'h-screen' : ''}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Execution Log
              </CardTitle>
              <div className="flex items-center gap-2">
                {execution.executionLog && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(execution.executionLog, '_blank')}
                    className="flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Original
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchLogContent(execution.executionLog!, 'execution')}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className={`${fullscreen ? 'h-full overflow-auto' : ''}`}>
              {logsLoading ? (
                <div className="flex justify-center items-center p-8">
                  <Spinner />
                </div>
              ) : logError ? (
                <div className="p-4 bg-red-50 text-red-700 rounded-md">
                  <p>{logError}</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 overflow-auto">
                  {executionLogContent ? (
                    formatCSVContent(executionLogContent)
                  ) : (
                    <div className="text-gray-500">No execution log content available</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ExecutionDetailsPage;
